import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

const labelStyles = {
  mb: "8px",
  fontWeight: 500,
};

const textFieldStyles = {
  backgroundColor: "#f8f8f8",
};

const buttonStyles = {
  backgroundColor: "#6c63ff",
  color: "#fff",
  ":hover": {
    backgroundColor: "#574fd6",
  },
};

export default function ContactForm() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    services: [],
  });
  const [selectedServices, setSelectedServices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState("success"); // "success" or "error"
  const [modalMessage, setModalMessage] = useState("");

  const servicesList = [
    "logo_design",
    "poster_design",
    "ui_design",
    "figma_design",
    "card_printing",
    "web_development",
    "mobile_development",
    "other",
  ];

  const handleServicesChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedServices(typeof value === "string" ? value.split(",") : value);
    setFormData((prev) => ({ ...prev, services: typeof value === "string" ? value.split(",") : value }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(
        "https://internal.ynetsolution.com/api/consultant/project",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      // Show success modal
      setModalStatus("success");
      setModalMessage(
        "Inquiry submitted successfully! Our team will get in touch with you."
      );
      setModalOpen(true);
      setLoading(false);

      // Reset form or steps if needed
      // setFormData({});
      // setSelectedServices([]);
      // setActiveStep(0);

      setTimeout(() => {
        setModalOpen(false);
      }, 10000);
    } catch (error) {
      setModalStatus("error");
      setModalMessage(
        "Error submitting the form. Please try later or contact us directly at +254794316045."
      );
      setModalOpen(true);
      setLoading(false);

      setTimeout(() => {
        setModalOpen(false);
      }, 20000);
    }
  };

  return (
    <>
      <Box
        className="contact-area"
        sx={{
          pb: { xs: "60px", sm: "60px", md: "80px", lg: "100px", xl: "150px" },
        }}
      >
        <Box
          sx={{
            maxWidth: {
              xs: "100%",
              sm: "700px",
              md: "720px",
              lg: "1140px",
              xl: "1320px",
            },
            mx: "auto",
            px: "12px",
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 2, md: 2, lg: 3 }}>
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <div className="fp-contact-image border-radius">
                <img
                  src="/images/front-pages/contact.jpg"
                  className="border-radius"
                  alt="contact-image"
                  width={813}
                  height={1083}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={6}>
              <div className="fp-contact-content">
                <div className="section-title">
                  <div className="sub-title">
                    <span className="d-inline-block position-relative text-purple">
                      Contact Us
                    </span>
                  </div>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: {
                        xs: "24px",
                        md: "28px",
                        lg: "34px",
                        xl: "36px",
                      },
                    }}
                  >
                    How Can We Help? We Love to Hear From You. Send Us a Message!
                  </Typography>
                </div>

                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit}>
                  {activeStep === 0 && (
                    <>
                      {[
                        { id: "name", label: "Full Name", required: true },
                        {
                          id: "email",
                          label: "Email Address",
                          required: true,
                          type: "email",
                        },
                        {
                          id: "phone",
                          label: "Phone Number",
                          required: true,
                        },
                        {
                          id: "company",
                          label: "Company (Optional)",
                        },
                      ].map(({ id, label, required, type = "text" }) => (
                        <Box mt="25px" key={id}>
                          <FormControl fullWidth>
                            <Typography component="label" sx={labelStyles}>
                              {label}
                            </Typography>
                            <TextField
                              label={`Your ${label.toLowerCase()}`}
                              variant="filled"
                              id={id}
                              name={id}
                              required={required}
                              type={type}
                              sx={textFieldStyles}
                              value={formData[id] || ""}
                              onChange={handleInputChange}
                            />
                          </FormControl>
                        </Box>
                      ))}

                      <Box mt="25px" display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          onClick={() => setActiveStep(1)}
                          disabled={
                            !formData.name || !formData.email || !formData.phone
                          }
                          sx={buttonStyles}
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}

                  {activeStep === 1 && (
                    <>
                      <Box mt="25px">
                        <FormControl fullWidth>
                          <Typography component="label" sx={labelStyles}>
                            Services Needed
                          </Typography>
                          <Select
                            id="services"
                            name="services"
                            multiple
                            value={selectedServices}
                            variant="filled"
                            sx={textFieldStyles}
                            onChange={handleServicesChange}
                            renderValue={(selected) => selected.join(", ")}
                          >
                            {servicesList.map((service) => (
                              <MenuItem key={service} value={service}>
                                <Checkbox checked={selectedServices.indexOf(service) > -1} />
                                <ListItemText primary={service.replace(/_/g, " ")} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {selectedServices.includes("other") && (
                        <Box mt="25px">
                          <FormControl fullWidth>
                            <Typography component="label" sx={labelStyles}>
                              Specify Other Service
                            </Typography>
                            <TextField
                              label="If you selected 'other', specify here"
                              variant="filled"
                              id="other_service"
                              name="other_service"
                              sx={textFieldStyles}
                              value={formData.other_service || ""}
                              onChange={handleInputChange}
                            />
                          </FormControl>
                        </Box>
                      )}

                      <Box mt="25px" display="flex" justifyContent="space-between">
                        <Button
                          variant="outlined"
                          onClick={() => setActiveStep(0)}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => setActiveStep(2)}
                          disabled={selectedServices.length === 0}
                          sx={buttonStyles}
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}

                  {/* Step 3: Project details */}
                  {activeStep === 2 && (
                    <>
                      <Box mt="25px">
                        <FormControl fullWidth>
                          <Typography component="label" sx={labelStyles}>
                            Project Name
                          </Typography>
                          <TextField
                            label="Name of the project"
                            variant="filled"
                            id="project_name"
                            name="project_name"
                            required
                            sx={textFieldStyles}
                            value={formData.project_name || ""}
                            onChange={handleInputChange}
                          />
                        </FormControl>
                      </Box>

                      <Box mt="25px">
                        <FormControl fullWidth>
                          <Typography component="label" sx={labelStyles}>
                            Project Description
                          </Typography>
                          <TextField
                            multiline
                            rows={4}
                            label="Describe your project"
                            variant="filled"
                            id="project_description"
                            name="project_description"
                            required
                            sx={textFieldStyles}
                            value={formData.project_description || ""}
                            onChange={handleInputChange}
                          />
                        </FormControl>
                      </Box>

                      <Box mt="25px">
                        <FormControl fullWidth>
                          <Typography component="label" sx={labelStyles}>
                            Deadline (Optional)
                          </Typography>
                          <TextField
                            type="date"
                            variant="filled"
                            id="deadline"
                            name="deadline"
                            sx={textFieldStyles}
                            value={formData.deadline || ""}
                            onChange={handleInputChange}
                            InputLabelProps={{
                            shrink: true,
                            }}
                            />
                            </FormControl>
                            </Box>
                            <Box mt="25px">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.agree || false}
                          onChange={handleInputChange}
                          name="agree"
                          required
                        />
                      }
                      label="I agree to the terms and conditions"
                    />
                  </Box>

                  <Box mt="25px" display="flex" justifyContent="space-between">
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      sx={buttonStyles}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </div>
        </Grid>
      </Grid>
    </Box>
  </Box>

  {/* Modal */}
  <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
    <DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {modalStatus === "success" ? "Success" : "Error"}
        <IconButton onClick={() => setModalOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Box textAlign="center" py={2}>
        {modalStatus === "success" ? (
          <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
        ) : (
          <ErrorOutlineIcon color="error" style={{ fontSize: 60 }} />
        )}
        <Typography mt={2}>{modalMessage}</Typography>
      </Box>
    </DialogContent>
  </Dialog>
</>
);
}